import React, { useState, useEffect } from 'react';
import { FiEdit, FiSave, FiX } from 'react-icons/fi';

const QuotationBody = ({ 
  leadId, 
  baseURL,
  onUpdateSuccess 
}) => {
  const [quotationBody, setQuotationBody] = useState('');
  const [editText, setEditText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQuotationBody = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`${baseURL}/api/leads/${leadId}/quotation-body`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch quotation body');
        }
        
        const data = await response.json();
        
        if (data.success) {
          setQuotationBody(data.data.quotation_body);
        } else {
          setError(data.message);
        }
      } catch (err) {
        console.error('Error fetching quotation body:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (leadId) {
      fetchQuotationBody();
    }
  }, [leadId, baseURL]);

  const handleEdit = () => {
    setIsEditing(true);
    setEditText(quotationBody);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditText(quotationBody);
    setError(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      const response = await fetch(`${baseURL}/api/leads/${leadId}/quotation-body`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quotation_body: editText
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update quotation body');
      }
      
      if (data.success) {
        setQuotationBody(data.data.quotation_body);
        setIsEditing(false);
        if (onUpdateSuccess) {
          onUpdateSuccess(data.data.quotation_body);
        }
        alert('Quotation body updated successfully!');
      } else {
        throw new Error(data.message);
      }
    } catch (err) {
      console.error('Error updating quotation body:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatQuotationBody = (text) => {
    if (!text) return 'No quotation body specified.';
    
    return (
      <div 
        style={{ 
          whiteSpace: 'pre-wrap',
          fontFamily: 'inherit'
        }}
      >
        {text}
      </div>
    );
  };

  return (
<>
      <div className="adminviewproducts-viewlead-d-flex adminviewproducts-viewlead-justify-content-between adminviewproducts-viewlead-align-items-center adminviewproducts-viewlead-mb-3">
        <h5 className="adminviewproducts-viewlead-mb-0">Quotation Body</h5>
        {!isEditing && (
          <button
            className="adminviewproducts-viewlead-btn adminviewproducts-viewlead-btn-outline-primary adminviewproducts-viewlead-btn-sm"
            onClick={handleEdit}
            disabled={loading}
          >
            <FiEdit className="adminviewproducts-viewlead-me-1" />
            Edit
          </button>
        )}
        {isEditing && (
          <div>
            <button
              className="adminviewproducts-viewlead-btn adminviewproducts-viewlead-btn-success adminviewproducts-viewlead-btn-sm adminviewproducts-viewlead-me-2"
              onClick={handleSave}
              disabled={saving}
            >
              <FiSave className="adminviewproducts-viewlead-me-1" />
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              className="adminviewproducts-viewlead-btn adminviewproducts-viewlead-btn-secondary adminviewproducts-viewlead-btn-sm"
              onClick={handleCancel}
              disabled={saving}
            >
              <FiX className="adminviewproducts-viewlead-me-1" />
              Cancel
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="adminviewproducts-viewlead-alert adminviewproducts-viewlead-alert-danger adminviewproducts-viewlead-mb-3">
          {error}
        </div>
      )}

      {loading ? (
        <div className="adminviewproducts-viewlead-text-center">
          <p>Loading quotation body...</p>
        </div>
      ) : isEditing ? (
        <div className="adminviewproducts-viewlead-card adminviewproducts-viewlead-card-body adminviewproducts-viewlead-bg-light">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="adminviewproducts-viewlead-form-control"
            rows="15"
            placeholder="Enter quotation body here..."
            style={{
              fontSize: '14px',
              lineHeight: '1.5',
              fontFamily: 'inherit'
            }}
          />
          <small className="adminviewproducts-viewlead-text-muted">
            Enter the complete quotation body text. This will be displayed as formatted text.
          </small>
        </div>
      ) : (
        <div 
          className="adminviewproducts-viewlead-card adminviewproducts-viewlead-card-body adminviewproducts-viewlead-bg-light"
          style={{ 
            fontSize: '14px',
            lineHeight: '1.5',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '15px'
          }}
        >
          {formatQuotationBody(quotationBody)}
        </div>
      )}
   </>
  );
};

export default QuotationBody;